export type ServiceHubType = 'transport' | 'delivery' | 'on_demand';

export type TabKey =
  | 'summary' | 'bookings' | 'manual-booking' | 'bidding' | 'tracking'
  | 'partners' | 'providers' | 'partner-payout' | 'users' | 'catalog'
  | 'reviews' | 'earnings' | 'settings' | 'pricing' | 'zones' | 'masters'
  | 'order-history' | 'scheduled-orders' | 'fleet' | 'routes'
  | 'slots' | 'schedule' | 'venues' | 'corporate' | 'quotations' | 'packages';

const SERVICE_USERS_TAB: FlowTab = { key: 'users', label: 'Users', icon: 'Group' };

const TRANSPORT_OPERATIONS_TABS: FlowTab[] = [
  SERVICE_USERS_TAB,
  { key: 'partners', label: 'Drivers', icon: 'People' },
];

export interface FlowTab {
  key: TabKey;
  label: string;
  icon: string;
}

export const TRANSPORT_FLOW_BY_SLUG: Record<string, { biddingEnabled: boolean; tabs: FlowTab[] }> = {
  transport: {
    biddingEnabled: true,
    tabs: [
      { key: 'summary', label: 'Summary', icon: 'Home' },
      { key: 'bookings', label: 'Rides', icon: 'LocalTaxi' },
      { key: 'manual-booking', label: 'Book Ride', icon: 'PostAdd' },
      { key: 'bidding', label: 'Bidding', icon: 'Gavel' },
      { key: 'reviews', label: 'Reviews', icon: 'Star' },
      { key: 'earnings', label: 'Earnings', icon: 'Payments' },
      ...TRANSPORT_OPERATIONS_TABS,
      { key: 'fleet', label: 'Rental packages', icon: 'Schedule' },
      { key: 'routes', label: 'Outstation routes', icon: 'Route' },
      { key: 'masters', label: 'Masters', icon: 'Storage' },
      { key: 'settings', label: 'Settings', icon: 'Settings' },
    ],
  },
};

const DELIVERY_FLOW_BY_SLUG: Record<string, FlowTab[]> = {
  'food-delivery': [
    { key: 'summary', label: 'Summary', icon: 'Home' },
    { key: 'bookings', label: 'Live Orders', icon: 'ShoppingBag' },
    { key: 'order-history', label: 'Order History', icon: 'ReceiptLong' },
    { key: 'scheduled-orders', label: 'Scheduled Orders', icon: 'EventNote' },
    { key: 'manual-booking', label: 'Manual Order', icon: 'PostAdd' },
    { key: 'catalog', label: 'Restaurants', icon: 'Store' },
    SERVICE_USERS_TAB,
    { key: 'partners', label: 'Delivery Partners', icon: 'DeliveryDining' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
    { key: 'earnings', label: 'Earnings', icon: 'Payments' },
    { key: 'masters', label: 'Masters', icon: 'Storage' },
    { key: 'settings', label: 'Settings', icon: 'Settings' },
  ],
  'grocery-delivery': [
    { key: 'summary', label: 'Summary', icon: 'Home' },
    { key: 'bookings', label: 'Live Orders', icon: 'ShoppingCart' },
    { key: 'order-history', label: 'Order History', icon: 'ReceiptLong' },
    { key: 'manual-booking', label: 'Manual Order', icon: 'PostAdd' },
    { key: 'catalog', label: 'Stores & Products', icon: 'Store' },
    SERVICE_USERS_TAB,
    { key: 'partners', label: 'Delivery Partners', icon: 'DeliveryDining' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
    { key: 'earnings', label: 'Earnings', icon: 'Payments' },
    { key: 'masters', label: 'Masters', icon: 'Storage' },
    { key: 'settings', label: 'Settings', icon: 'Settings' },
  ],
  'porter-service': [
    { key: 'summary', label: 'Summary', icon: 'Home' },
    { key: 'bookings', label: 'Live Orders', icon: 'LocalShipping' },
    { key: 'order-history', label: 'Order History', icon: 'ReceiptLong' },
    { key: 'scheduled-orders', label: 'Scheduled Orders', icon: 'EventNote' },
    { key: 'manual-booking', label: 'Manual Booking', icon: 'PostAdd' },
    { key: 'fleet', label: 'Vehicles', icon: 'LocalShipping' },
    { key: 'catalog', label: 'Categories', icon: 'Category' },
    { key: 'packages', label: 'Packages', icon: 'Inventory' },
    SERVICE_USERS_TAB,
    { key: 'partners', label: 'Partners', icon: 'People' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
    { key: 'earnings', label: 'Earnings', icon: 'Payments' },
    { key: 'masters', label: 'Masters', icon: 'Storage' },
    { key: 'settings', label: 'Settings', icon: 'Settings' },
  ],
};

const ON_DEMAND_FLOW_BY_SLUG: Record<string, FlowTab[]> = {
  'home-services': [
    { key: 'masters', label: 'Masters', icon: 'Storage' },
    { key: 'summary', label: 'Summary', icon: 'Home' },
    { key: 'bookings', label: 'Bookings', icon: 'Handyman' },
    { key: 'quotations', label: 'Quotations', icon: 'ReceiptLong' },
    { key: 'manual-booking', label: 'Manual Booking', icon: 'PostAdd' },
    { key: 'catalog', label: 'Packages', icon: 'Store' },
    { key: 'tracking', label: 'Live Tracking', icon: 'MyLocation' },
    SERVICE_USERS_TAB,
    { key: 'partners', label: 'Service Providers', icon: 'Handyman' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
    { key: 'earnings', label: 'Earnings', icon: 'Payments' },
    { key: 'zones', label: 'Zones & Coverage', icon: 'Map' },
    { key: 'settings', label: 'Settings', icon: 'Settings' },
  ],
};

const HUB_TABS: Record<Exclude<ServiceHubType, 'transport'>, FlowTab[]> = {
  delivery: [
    { key: 'summary', label: 'Summary', icon: 'Home' },
    { key: 'bookings', label: 'Orders', icon: 'ShoppingBag' },
    { key: 'manual-booking', label: 'Manual Order', icon: 'PostAdd' },
    { key: 'catalog', label: 'Catalog / Stores', icon: 'Store' },
    SERVICE_USERS_TAB,
    { key: 'partners', label: 'Delivery Partners', icon: 'DeliveryDining' },
    { key: 'zones', label: 'Zones & Coverage', icon: 'Map' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
    { key: 'earnings', label: 'Earnings', icon: 'Payments' },
    { key: 'pricing', label: 'Pricing Rules', icon: 'LocalOffer' },
    { key: 'masters', label: 'Masters', icon: 'Storage' },
    { key: 'settings', label: 'Settings', icon: 'Settings' },
  ],
  on_demand: ON_DEMAND_FLOW_BY_SLUG['home-services'],
};

export function resolveServiceTabs(
  hubType: ServiceHubType,
  slug?: string,
  platformModule?: string,
): FlowTab[] {
  let tabs: FlowTab[];
  if (hubType === 'transport' && slug && TRANSPORT_FLOW_BY_SLUG[slug]) {
    tabs = TRANSPORT_FLOW_BY_SLUG[slug].tabs;
  } else if (hubType === 'on_demand' && slug && ON_DEMAND_FLOW_BY_SLUG[slug]) {
    tabs = ON_DEMAND_FLOW_BY_SLUG[slug];
  } else if (hubType === 'on_demand') {
    tabs = HUB_TABS.on_demand;
  } else if (hubType === 'delivery' && slug && DELIVERY_FLOW_BY_SLUG[slug]) {
    tabs = DELIVERY_FLOW_BY_SLUG[slug];
  } else if (hubType === 'delivery') {
    const module = platformModule
      || (slug?.includes('porter') ? 'porter'
        : slug?.includes('grocery') ? 'grocery'
          : slug?.includes('food') ? 'food' : '');
    if (module === 'porter') tabs = DELIVERY_FLOW_BY_SLUG['porter-service'];
    else tabs = DELIVERY_FLOW_BY_SLUG['porter-service'];
  } else if (hubType !== 'transport') {
    tabs = HUB_TABS[hubType];
  } else {
    tabs = TRANSPORT_FLOW_BY_SLUG.transport.tabs;
  }
  return orderServiceTabs(tabs, hubType);
}

/** Masters is the default landing tab for every service workspace. */
function orderServiceTabs(tabs: FlowTab[], _hubType: ServiceHubType): FlowTab[] {
  const summary = tabs.find((t) => t.key === 'summary');
  const masters = tabs.find((t) => t.key === 'masters');
  const settings = tabs.find((t) => t.key === 'settings');
  const rest = tabs.filter((t) => t.key !== 'summary' && t.key !== 'masters' && t.key !== 'settings');
  const ordered: FlowTab[] = [];
  if (masters) ordered.push(masters);
  if (summary) ordered.push(summary);
  ordered.push(...rest);
  if (settings) ordered.push(settings);
  return ordered;
}
